import { AccordionItem, Alert } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { orderedConnectionsSelector } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { ReactComponent as IntegrationsBacksplash } from 'assets/feature-svgs/integrations.svg';
import { LinkOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Grid,
  GridLabels,
  GridRow,
} from '../../../../../common/components/Grid/Grid';
import { TableLabel } from '../../../../../common/components/TableGrid/TableGrid';
import { autoIncrementedName } from '../../../../../common/helpers/name-autoincrement';
import { INTEGRATIONS_SECTIONS } from '../../../beta/modules/Integrations/constants';
import {
  ComponentDataItem,
  DefaultCell,
} from '../../../contacts/components/ContactsTable/ContactTable';
import { ReactComponent as WebhookIcon } from './webhookIcon.svg';
import './ConnectionsPanel.pcss';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useFetchConnections } from '../../../beta/modules/Integrations/use-fetch-connections';

type Props = {
  cellData: ComponentDataItem[];
};

export const UNTITLED_CONNECTION_NAME = 'Untitled connection';

export const ConnectionsPanel = (props: Props) => {
  const { cellData } = props;

  const history = useHistory();
  const connections = useSelector(orderedConnectionsSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const connectedPageId = useSelector(activeFacebookPageIdSelector);

  const connectionsCRUDActions = useConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const modalActions = useModalActions();

  const { pagination } = useFetchConnections();

  const hasConnectionsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).jsonWebhook;
  const gridTemplateColumns = `20% 15% 20% 20% 15% 10%`;

  const onCloseHandler = async (isZapier: boolean) => {
    if (isZapier) {
      const zapierSection = INTEGRATIONS_SECTIONS.find(
        ({ id }) => id === 'zapier',
      );
      const betaPath = zapierSection?.path(chatbotId) || '';
      return history.push(betaPath);
    }
    const name = autoIncrementedName(
      UNTITLED_CONNECTION_NAME,
      connections.map(c => c.name),
    );

    const connection = await connectionsCRUDActions.create(connectedPageId, {
      name,
      connectionType: 'Webhook',
      description: '',
    });

    if (connection) {
      showSlideoutMenu('editJSONWebhookIntegrationEditor', {
        webhookId: connection.id,
      });
    }
  };

  const confirmNotZapier = () =>
    modalActions.openGeneralConfirmModal({
      headerText: 'Wait! Are you using Zapier?',
      okButtonText: "I'm using Zapier",
      okButtonStyling: { blue: true },
      cancelButtonText: "No, I'm not",
      cancelButtonFirst: true,
      onCloseHandler,
      messageText: (
        <p className="integrations__confirm-modal-p">
          You no longer have to create custom Webhooks to use Zapier with
          MobileMonkey! Click "I'm using Zapier" below to go to our
          full-featured Zapier integration instead.
        </p>
      ),
      css: {
        header: 'integrations__confirm-modal-header',
        primaryButton: 'integrations__confirm-modal-primary-button',
        secondaryButton: 'integrations__confirm-modal-secondary-button',
      },
    });

  const isUpgradeNeeded = !hasConnectionsFeature;

  const isUpgradeAlertNeeded = !!connections.length && isUpgradeNeeded;

  const showUpgradeModal = (isNoLongerAvailable = false) => {
    const landmineName = isNoLongerAvailable
      ? `${landmineNames.FEATURE_NO_LONGER_AVAILABLE}_${landmineNames.INTEGRATIONS_JSON_WEBHOOKS}`
      : landmineNames.INTEGRATIONS_JSON_WEBHOOKS;

    modalActions.openUniversalUpgradeLandmineModal({
      landmineName,
      planFallback: PageBillingType.ProUnicorn,
      feature: 'jsonWebhook',
    });
  };

  const onNewConnection = e => {
    e.stopPropagation();
    if (isUpgradeNeeded) {
      showUpgradeModal();
    } else {
      confirmNotZapier();
    }
  };

  const header = (
    <div
      className={classNames('integrations__header', {
        'integrations__header--account-connected': connections.length,
      })}
    >
      <div className="integrations__header-main">
        <div className="integrations__header-icon integrations__webhook-header-icon">
          <WebhookIcon />
        </div>
        <div>
          <div className="integrations__header-title">
            JSON Webhooks Integration
          </div>
          <div className="integrations__header-info">
            <div className="integrations__header-learn-more">
              Automatically send data from MobileMonkey to JSON Webhook
            </div>
          </div>
        </div>
      </div>

      <div className="integrations__header-actions">
        <div className="integrations__header-status">
          Connections
          <div className="integrations__header-status-count">
            {pagination?.total || 0}
          </div>
          <LinkOutlined className="integrations__header-status-icon" />
        </div>
        <PrimaryButton
          className="integrations__header-add-button"
          onClick={onNewConnection}
          color={ButtonColor.blue}
        >
          Add a Connection
        </PrimaryButton>
      </div>
    </div>
  );

  return (
    <>
      {isUpgradeAlertNeeded && (
        <Alert className="connections-panel__alert" showCloseIcon={false}>
          You no longer have access to this Startup feature. To continue using
          connections,{' '}
          <span
            className="connections-panel__alert-link"
            onClick={() => showUpgradeModal(true)}
          >
            Upgrade to Startup
          </span>
        </Alert>
      )}
      <div className="integrations-component">
        <AccordionItem
          className="integrations-item"
          caretStyleIcon
          closeOnOutsideClick={false}
          openOnMount={!!connections.length}
          titleComponent={header}
        >
          {!connections.length ? (
            <div className="integrations-empty-backspalsh">
              <IntegrationsBacksplash />
              <div className="integrations-empty-backspalsh-cta">
                There are no connections yet
              </div>
            </div>
          ) : (
            <Grid className="integrations-grid">
              {!!connections.length && (
                <>
                  <GridLabels
                    className="integrations-grid__labels connections-panel__grid-labels"
                    gridTemplateColumns={gridTemplateColumns}
                  >
                    {cellData.map(cell => (
                      <TableLabel
                        key={cell.keyName || cell.label}
                        dataItem={cell}
                        orderByAtTheEnd
                      />
                    ))}
                  </GridLabels>

                  <div className="integrations-grid__rows-container">
                    {connections.map(item => (
                      <GridRow
                        key={item.id}
                        gridTemplateColumns={gridTemplateColumns}
                      >
                        {cellData.map(({ Cell, keyName }) => {
                          return Cell ? (
                            <Cell key={keyName} item={item} />
                          ) : (
                            <DefaultCell
                              keyName={keyName as string}
                              item={item}
                            />
                          );
                        })}
                      </GridRow>
                    ))}
                  </div>
                </>
              )}
            </Grid>
          )}
        </AccordionItem>
      </div>
    </>
  );
};
